import { useState, useEffect, useRef } from 'react'
import { Bug, Trash2, X, ChevronDown, ChevronRight } from 'lucide-react'
import { devLog } from '../../services/devLog'
import type { LogEntry } from '../../services/devLog'

const LEVEL_COLORS: Record<LogEntry['level'], string> = {
  info: 'text-blue-400',
  warn: 'text-amber-400',
  error: 'text-red-400',
  request: 'text-emerald-400',
  response: 'text-cyan-400',
}

const LEVEL_PREFIX: Record<LogEntry['level'], string> = {
  info: 'INF',
  warn: 'WRN',
  error: 'ERR',
  request: '>>',
  response: '<<',
}

function LogLine({ entry }: { entry: LogEntry }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className="px-1.5 py-0.5 hover:bg-neutral-800/50 cursor-pointer font-mono"
      onClick={() => entry.detail && setExpanded(!expanded)}
    >
      <div className="flex items-start gap-1 text-[10px] leading-tight">
        <span className="text-neutral-600 shrink-0">{entry.timestamp}</span>
        <span className={`shrink-0 ${LEVEL_COLORS[entry.level]}`}>
          {LEVEL_PREFIX[entry.level]}
        </span>
        <span className="text-neutral-200 break-all">{entry.message}</span>
        {entry.detail && (
          expanded
            ? <ChevronDown size={10} className="shrink-0 text-neutral-600 mt-0.5" />
            : <ChevronRight size={10} className="shrink-0 text-neutral-600 mt-0.5" />
        )}
      </div>
      {expanded && entry.detail && (
        <div className="ml-12 text-[9px] text-neutral-500 break-all mt-0.5">
          {entry.detail}
        </div>
      )}
    </div>
  )
}

/**
 * Toggleable debug panel that shows devLog entries in the task pane.
 * Only visible in dev/preview environments, or when toggled via bug icon.
 */
export function DevPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const [entries, setEntries] = useState<LogEntry[]>(devLog.getEntries())
  const logEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return devLog.subscribe(() => {
      setEntries(devLog.getEntries())
    })
  }, [])

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (isOpen && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [entries, isOpen])

  return (
    <>
      {/* Toggle button — fixed bottom-right above tab bar */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-12 right-2 z-50 p-1.5 rounded-full shadow-lg transition-colors ${
          isOpen
            ? 'bg-red-600 text-white'
            : entries.some((e) => e.level === 'error')
              ? 'bg-red-600/90 text-white animate-pulse'
              : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
        }`}
        title="Toggle dev log"
      >
        <Bug size={14} />
      </button>

      {/* Log panel */}
      {isOpen && (
        <div className="fixed inset-0 bottom-10 z-40 flex flex-col bg-neutral-900/95 backdrop-blur-sm">
          {/* Header */}
          <div className="flex items-center justify-between px-2 py-1.5 border-b border-neutral-700">
            <span className="text-[11px] font-bold text-neutral-300">
              Dev Log ({entries.length})
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => devLog.clear()}
                className="p-0.5 text-neutral-500 hover:text-neutral-300"
                title="Clear log"
              >
                <Trash2 size={12} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-0.5 text-neutral-500 hover:text-neutral-300"
                title="Close"
              >
                <X size={12} />
              </button>
            </div>
          </div>

          {/* Entries */}
          <div className="flex-1 overflow-y-auto">
            {entries.length === 0 ? (
              <div className="p-4 text-center text-[10px] text-neutral-600">
                No log entries yet. Interact with the add-in to see logs.
              </div>
            ) : (
              entries.map((entry) => (
                <LogLine key={entry.id} entry={entry} />
              ))
            )}
            <div ref={logEndRef} />
          </div>
        </div>
      )}
    </>
  )
}
